// @ts-strict-ignore
import React, {
  useCallback, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import type { TableState } from '@tanstack/table-core';
import { datadogRum } from '@datadog/browser-rum';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Box,
  ButtonSize,
  ButtonVariant,
  IconName,
  StyledBulkAction,
  Table,
  makeInitialTableState,
} from '@usealloy/component-library';

import {
  getAllSortedResults,
  getSortingKeyString,
} from '../../helpers';
import type { FilterOptions, MatchData } from '../../types';
import { isResolvable } from '../ResolutionModal/helper';
import { calculateWatchlistTableProps } from './helpers';

interface WatchlistTableProps {
  columns: ColumnDef<MatchData>[];
  enableResolution?: boolean;
  filteredMatches: MatchData[];
  filterOptions: FilterOptions;
  onRowClick(match: MatchData): void;
  openModal(match: MatchData[]): void;
  selectedRows: any;
  setSelectedRows: any;
  scrollToRightOnLoad?: boolean;
}

const WatchlistTable = ({
  columns,
  enableResolution,
  filteredMatches,
  filterOptions: {
    screeningTypeOptions,
  },
  onRowClick,
  openModal,
  selectedRows,
  setSelectedRows,
  scrollToRightOnLoad = false,
}: WatchlistTableProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const didInitialScrollRef = useRef(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [selectedMatches, setSelectedMatches] = useState<MatchData[]>([]);
  const [columnsState, setColumnsState] = useState<TableState>(makeInitialTableState());
  const [tableSorting, setTableSorting] = useState([]);

  const tableColumns = useMemo(() => {
    const hasNonSourceScreeningTypes = screeningTypeOptions?.some(({ label }) => !['All', 'Source'].includes(label));
    if (hasNonSourceScreeningTypes) {
      return columns;
    }
    return columns.filter(column => column.id !== 'screeningType');
  }, [columns, screeningTypeOptions]);

  const tableState = useMemo(() => ({
    ...columnsState,
    pagination: { pageIndex, pageSize: 10 },
    rowSelection: selectedRows,
    sorting: tableSorting,
  }), [columnsState, tableSorting, pageIndex, selectedRows]);

  const [
    sortedFilteredData,
    sortedFilteredDataPage,
  ] = useMemo(() => {
    const tableOutput = tableSorting.length
      ? getAllSortedResults(tableSorting, filteredMatches)
      : filteredMatches;

    return [
      tableOutput,
      tableOutput.slice(pageIndex * 10, (pageIndex + 1) * 10),
    ];
  }, [filteredMatches, tableSorting, pageIndex]);

  const handleChangeTableState = useCallback(nextTableState => {
    setColumnsState(nextTableState);
    const { pagination, rowSelection, sorting } = nextTableState;
    if (rowSelection !== selectedRows) {
      setSelectedRows(rowSelection);
      const rowsKeys = Object.keys(rowSelection);
      const newSelectedMatches = rowsKeys.map(key => sortedFilteredData[key]);
      setSelectedMatches(newSelectedMatches);
    }
    if (pageIndex !== pagination.pageIndex) {
      setPageIndex(pagination.pageIndex);
    }
    if (getSortingKeyString(sorting) !== getSortingKeyString(tableSorting)) {
      setTableSorting(sorting);
    }
  }, [selectedRows, pageIndex, tableSorting, sortedFilteredData]);

  const conditionalProps = useMemo(() => {
    const {
      hasResolvableServices,
    } = calculateWatchlistTableProps(filteredMatches);
    const props = {} as { [key: string]: Function | null };
    if (enableResolution && hasResolvableServices) {
      props.enableRowSelection = row => isResolvable(row.original);
    }
    return props;
  }, [enableResolution, filteredMatches]);

  useLayoutEffect(() => {
    if (!scrollToRightOnLoad) return;
    // On initial mount, scroll the horizontally-scrollable table wrapper all the way right
    // so rightmost columns (e.g. AI Recommendation / Status / Note) are visible on load.
    if (didInitialScrollRef.current) return;

    const root = containerRef.current;
    if (!root) return;

    // Table's internal scroll container is a div with inline overflowX: 'auto'
    const scrollEl = root.querySelector<HTMLDivElement>('div[style*="overflow-x: auto"]');
    if (!scrollEl) return;

    didInitialScrollRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollEl.scrollLeft = scrollEl.scrollWidth;
      });
    });
  }, [scrollToRightOnLoad]);

  return (
    <Box
      ref={containerRef}
      data-testId="watchlist-table"
      direction="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      style={{ width: '100%' }}
      className="watchlist-list-table-wrapper"
      id="watchlist-list-table-wrapper"
    >
      <Table
        bulkActions={[
          {
            render: () => (
              <StyledBulkAction
                key="watchlistMatches-bulk-resolve"
                icon={IconName.Approved}
                onClick={() => { openModal(selectedMatches); }}
                size={ButtonSize.small}
                variant={ButtonVariant.tertiary}
                data-dd-action-name="alloydd-watchlistMatches-resolve"
                disabled={!enableResolution}
                hoverBackgroundColor={!enableResolution && 'transparent'}
              >
                Resolve
              </StyledBulkAction>
            ),
          },
        ]}
        columns={tableColumns}
        data={sortedFilteredDataPage}
        enablePagination
        stickyPagination
        enableSorting
        disablePageSizeSelection
        onRowClick={(_, row) => {
          datadogRum.addAction('watchlistMatches-openMatch');
          return onRowClick(row);
        }}
        onTableStateChange={handleChangeTableState}
        recordCount={filteredMatches.length}
        state={tableState}
        enableColumnHiding
        enableColumnResizing
        {...conditionalProps}
      />
    </Box>
  );
};

export default WatchlistTable;
